import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Button, Checkbox, Input, Loader, Radio } from 'components/UIElements'
import { formatAndSanitizeHTML } from 'utils/richText'
import { ACTION_LABELS } from '../modules/DataCorrectionV2'
import {
  DCF_STATUS,
  APPLYABLE_STATUSES,
  canApplyDcf,
  canEditDcfContent,
  canEditSignatures,
  canRejectDcf,
  isTerminalDcf,
} from '../../../utils/dcfStatuses'
import formatDcfErrorMessage from '../../../utils/dcfErrorMessage'
import { parseChoiceValueList } from '../../../utils/surveyQuestions'
import PdfViewerWithSigning from './PdfViewerWithSigning'
import { encryptPasswordForReauth } from '../../../utils/reauthDcfSigner'
import {
  getDcfSignerPrefillFromUser,
  resolveNextDcfSignatureRoleForUser,
  userCanSignAllDcfRoles,
} from '../../../utils/dcfSignerRole'

const SINGLE_CHOICE_TYPES = new Set([
  'select_one',
  'long_list_select_one',
  'likert',
  'numeric_rating_scale',
  'nrs',
  'horizontal_select_one',
])
const SIGNATURE_ROLES = [
  {
    role: 'site_approval',
    label: 'Site Approval',
    hint: 'External or site stakeholder. Decision is stamped on the PDF.',
    requiresDecision: true,
  },
  {
    role: 'dch_approval',
    label: 'Datacubed Health Approval',
    hint: 'Datacubed Health approver (name may be external).',
  },
  {
    role: 'reviewed_by',
    label: 'Reviewed By',
    hint: 'Impact assessment reviewer.',
  },
  {
    role: 'verified_by',
    label: 'Verified By',
    hint: 'Verification sign-off.',
  },
]

const signatureRoleLabel = role =>
  SIGNATURE_ROLES.find(roleConfig => roleConfig.role === role)?.label || role

const isDetailPayload = value => Boolean(value) && typeof value === 'object' && Boolean(value.id)

const toInputDateTime = value => {
  if (!value || !moment(value).isValid()) return ''
  return moment(value).format('YYYY-MM-DDTHH:mm')
}

const toIsoDateTime = value => {
  if (!value || !moment(value).isValid()) return null
  return moment(value).toISOString()
}

const formatDateTime = value => {
  if (!value || !moment(value).isValid()) return '—'
  return moment(value).format('MM/DD/YYYY h:mm A')
}

const createDraft = detail => ({
  instrumentVersion: detail?.instrument_version || '',
  startBy: toInputDateTime(detail?.start_by || detail?.started_by),
  completedBy: toInputDateTime(detail?.completed_by),
  answers: (detail?.answers || []).map(answer => ({
    ...answer,
    answer_id: answer.answer_id ?? null,
    original_answer_id: answer.original_answer_id ?? null,
    corrected_answer: answer.corrected_answer ?? '',
  })),
})

const parseJsonStringArray = value => parseChoiceValueList(value)

const renderChoiceLabel = label => (
  <span
    className='choice-label'
    // Content is sanitized with DOMPurify immediately before rendering.
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{ __html: formatAndSanitizeHTML(label || '') }}
  />
)

const DcfReviewDrawer = ({
  dcfDetail,
  dcfLookups,
  studyID,
  currentUser = null,
  loading = false,
  closeDrawer,
  updateDcfStatus,
  updateDcfContent,
  upsertDcfSignatures,
  deleteDcfSignature,
  applyDcf,
  downloadDcf,
  downloadDcfCsv,
  fetchDcfPdfBytes,
  fetchReauthPubkey,
  postSigningReauth,
  uploadSignedDcfPdf,
  fetchDcfList,
  fetchDcfDetail,
  fetchInstrumentSurvey,
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(() => createDraft(dcfDetail))
  const [signatureMessage, setSignatureMessage] = useState(null)
  const [questionMetaById, setQuestionMetaById] = useState({})
  const [esignPdfBytes, setEsignPdfBytes] = useState(null)

  const savedSignaturesByRole = useMemo(() => {
    const byRole = {}
    ;(dcfDetail?.signatures || []).forEach(signature => {
      byRole[signature.role] = signature
    })
    return byRole
  }, [dcfDetail])

  const signingRoleConfig = useMemo(
    () =>
      resolveNextDcfSignatureRoleForUser(
        currentUser,
        studyID,
        Object.keys(savedSignaturesByRole),
      ),
    [currentUser, studyID, savedSignaturesByRole],
  )
  const canCycleAllRoles = useMemo(
    () => userCanSignAllDcfRoles(currentUser, studyID),
    [currentUser, studyID],
  )
  const signerPrefill = useMemo(() => getDcfSignerPrefillFromUser(currentUser), [currentUser])

  useEffect(() => {
    if (!isEditing) setDraft(createDraft(dcfDetail))
  }, [dcfDetail, isEditing])

  useEffect(() => {
    let cancelled = false
    if (isEditing && studyID && dcfDetail?.instrument_id && fetchInstrumentSurvey) {
      fetchInstrumentSurvey(studyID, dcfDetail.instrument_id).then(byId => {
        if (!cancelled && byId) setQuestionMetaById(byId)
      })
    }
    return () => {
      cancelled = true
    }
  }, [isEditing, studyID, dcfDetail?.instrument_id, fetchInstrumentSurvey])

  useEffect(() => {
    if (!studyID || !dcfDetail?.id || !fetchDcfPdfBytes) return undefined
    let cancelled = false
    fetchDcfPdfBytes(studyID, dcfDetail.id).then(bytes => {
      if (!cancelled && bytes) setEsignPdfBytes(bytes)
    })
    return () => {
      cancelled = true
    }
  }, [studyID, dcfDetail?.id, dcfDetail?.signatures, fetchDcfPdfBytes])

  if (!dcfDetail) {
    return <div className='dcf-review-drawer'>No DCF details available.</div>
  }

  const {
    id,
    participant_id: participantId,
    subject_id: subjectId,
    participant_subject_id: participantSubjectId,
    instrument_id: instrumentId,
    instrument_name: instrumentName,
    instrument_title: instrumentTitle,
    instrument_version: instrumentVersion,
    completed_by: completedBy,
    status,
    action,
    start_by: startByValue,
    started_by: startedByLegacy,
    error_message: errorMessage,
    answers,
    instance_id: instanceId,
    transfer_ignore: transferIgnore,
    reporting_ignore: reportingIgnore,
  } = dcfDetail

  const displaySubjectId =
    subjectId ||
    participantSubjectId ||
    dcfLookups?.participants?.[String(participantId)] ||
    participantId
  const displayInstrumentName =
    dcfLookups?.instruments?.[String(instrumentId)] ||
    instrumentName ||
    instrumentTitle ||
    instrumentId
  const startBy = startByValue || startedByLegacy
  const actionLabel = ACTION_LABELS[action] || action || '—'
  const displayErrorMessage = formatDcfErrorMessage(errorMessage)
  const canMarkInReview = status === DCF_STATUS.SUBMITTED
  const canCompleteReview = status === DCF_STATUS.IN_REVIEW
  const canSubmitDraft = status === DCF_STATUS.IN_DRAFT
  const canReject = canRejectDcf(status)
  const isApplyableStatus = APPLYABLE_STATUSES.has(status)
  const canApply = canApplyDcf(status, dcfDetail)
  const canEdit = canEditDcfContent(status)
  const canSign = canEditSignatures(status)
  const isTerminal = isTerminalDcf(status)
  const statusClass = (status || '').toLowerCase().replace(/_/g, '-')
  const statusLabel = (status || 'unknown').replace(/_/g, ' ')

  const refresh = () => {
    if (studyID && id) {
      // Status/apply actions already showed a loader; quiet refresh avoids a second flash.
      fetchDcfDetail(studyID, id, { showLoader: false })
      fetchDcfList(studyID, { showLoader: false })
    }
  }

  const onSubmitDraft = () => {
    updateDcfStatus(studyID, id, DCF_STATUS.SUBMITTED).then(refresh)
  }

  const onMarkInReview = () => {
    updateDcfStatus(studyID, id, DCF_STATUS.IN_REVIEW).then(refresh)
  }

  const onReject = () => {
    updateDcfStatus(studyID, id, DCF_STATUS.REJECTED).then(() => {
      refresh()
      closeDrawer()
    })
  }

  const onCompleteReview = () => {
    updateDcfStatus(studyID, id, DCF_STATUS.APPROVED).then(refresh)
  }

  const onApply = () => {
    applyDcf(studyID, id).then(() => {
      refresh()
    })
  }

  const onDownloadPdf = () => {
    if (studyID && id) downloadDcf(studyID, id)
  }

  const onDownloadCsv = () => {
    if (studyID && id && downloadDcfCsv) downloadDcfCsv(studyID, id)
  }

  const verifyEsignPassword = async password => {
    const pubkey = await fetchReauthPubkey()
    const publicKeyPem = pubkey?.public_key_pem
    const keyId = pubkey?.key_id
    if (!publicKeyPem) throw new Error('Signing public key is not configured')
    const passwordEncrypted = await encryptPasswordForReauth(password, publicKeyPem)
    const result = await postSigningReauth({
      password_encrypted: passwordEncrypted,
      key_id: keyId,
    })
    if (!result?.reauth_proof) throw new Error('Password verification failed')
    return result
  }

  const onEsignComplete = async payload => {
    const {
      role,
      signerName,
      signerTitle,
      signerEmail,
      signatureType,
      signatureData,
      decision,
      placement,
      reauthProof,
      reauthJti,
      reauthVerifiedAt,
      stampedPdfBytes,
    } = payload
    await uploadSignedDcfPdf(studyID, id, stampedPdfBytes, reauthProof)
    const signature = {
      role,
      signer_name: signerName,
      signer_title: signerTitle || null,
      signer_email: signerEmail || null,
      signature_type: signatureType || 'draw',
      signature_data: signatureData,
      page: placement.page,
      x_pct: placement.xPct,
      y_pct: placement.yPct,
      reauth_verified_at: reauthVerifiedAt,
      reauth_jti: reauthJti,
      pdf_client_stamped: true,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    }
    if (role === 'site_approval') signature.decision = decision || 'approved'
    const savedDetail = await upsertDcfSignatures(studyID, id, [signature], {
      pdfClientStamped: true,
      reauthProof,
    })
    if (!isDetailPayload(savedDetail)) {
      throw new Error('Failed to save DCF signature metadata')
    }
    setEsignPdfBytes(stampedPdfBytes)
    setSignatureMessage({
      isError: false,
      text: `${signatureRoleLabel(role)} stamped on the PDF.`,
    })
    refresh()
  }

  const onRemoveSignature = role => {
    deleteDcfSignature(studyID, id, role).then(detail => {
      if (!isDetailPayload(detail)) {
        setSignatureMessage({
          isError: true,
          text: `Could not remove the ${signatureRoleLabel(role)} signature.`,
        })
        return
      }
      setSignatureMessage({
        isError: false,
        text: `${signatureRoleLabel(role)} signature removed. You can capture it again.`,
      })
      refresh()
    })
  }

  const updateDraftField = (field, value) => {
    setDraft(current => ({ ...current, [field]: value }))
  }

  const updateDraftAnswer = (index, value, meta = {}) => {
    setDraft(current => ({
      ...current,
      answers: current.answers.map((answer, answerIndex) =>
        answerIndex === index
          ? {
              ...answer,
              corrected_answer: value,
              ...(Object.prototype.hasOwnProperty.call(meta, 'answer_id')
                ? { answer_id: meta.answer_id }
                : {}),
            }
          : answer,
      ),
    }))
  }

  const selectDraftChoice = (index, choice) => {
    updateDraftAnswer(index, choice.value, { answer_id: choice.id })
  }

  const toggleDraftMultipleChoice = (index, answer, choice) => {
    const selectedIds = parseJsonStringArray(answer.answer_id)
    const selectedValues = parseJsonStringArray(answer.corrected_answer)
    const isSelected = selectedIds.includes(choice.id) || selectedValues.includes(choice.value)
    const nextIds = isSelected
      ? selectedIds.filter(choiceId => choiceId !== choice.id)
      : [...selectedIds.filter(choiceId => choiceId !== choice.id), choice.id]
    const nextValues = isSelected
      ? selectedValues.filter(value => value !== choice.value)
      : [...selectedValues.filter(value => value !== choice.value), choice.value]
    updateDraftAnswer(index, JSON.stringify(nextValues), {
      answer_id: JSON.stringify(nextIds),
    })
  }

  const renderCorrectedAnswerEditor = (answer, draftAnswer, index) => {
    const questionMeta = questionMetaById[answer.question_id]
    const choices = questionMeta?.choices || []
    const questionType = questionMeta?.questionType
    const selectedAnswerId =
      draftAnswer.answer_id != null ? String(draftAnswer.answer_id) : ''
    const value = draftAnswer.corrected_answer ?? ''

    if (questionType && SINGLE_CHOICE_TYPES.has(questionType) && choices.length > 0) {
      const isScale = ['likert', 'numeric_rating_scale', 'nrs', 'horizontal_select_one'].includes(
        questionType,
      )
      return (
        <div className={`survey-choice-list${isScale ? ' scale-choice-list' : ''}`}>
          {choices.map(choice => (
            <Radio
              key={choice.id}
              id={`edit-dcf-answer-${answer.question_id}-${choice.id}`}
              selected={
                selectedAnswerId === choice.id ||
                (!selectedAnswerId && String(value) === choice.value)
              }
              content={renderChoiceLabel(choice.label)}
              onClick={() => selectDraftChoice(index, choice)}
            />
          ))}
        </div>
      )
    }

    if (questionType === 'select_multiple' && choices.length > 0) {
      const selectedIds = parseJsonStringArray(draftAnswer.answer_id)
      const selectedValues = parseJsonStringArray(value)
      return (
        <div className='survey-choice-list'>
          {choices.map(choice => (
            <Checkbox
              key={choice.id}
              id={`edit-dcf-answer-${answer.question_id}-${choice.id}`}
              checked={selectedIds.includes(choice.id) || selectedValues.includes(choice.value)}
              label={renderChoiceLabel(choice.label)}
              onClick={() => toggleDraftMultipleChoice(index, draftAnswer, choice)}
            />
          ))}
        </div>
      )
    }

    return (
      <Input
        id={`edit-dcf-answer-${answer.question_id}`}
        value={value}
        onChange={nextValue => updateDraftAnswer(index, nextValue)}
      />
    )
  }

  const onSave = () => {
    const body = {
      instrument_version: draft.instrumentVersion || null,
      start_by: toIsoDateTime(draft.startBy),
      completed_by: toIsoDateTime(draft.completedBy),
      answers: draft.answers.map(answer => ({
        question_id: answer.question_id,
        question_number: answer.question_number,
        question_label: answer.question_label,
        original_answer_id: answer.original_answer_id ?? null,
        answer_id: answer.answer_id ?? null,
        original_answer: answer.original_answer,
        corrected_answer: answer.corrected_answer,
      })),
    }

    updateDcfContent(studyID, id, body).then(detail => {
      if (!detail) return
      setIsEditing(false)
      fetchDcfList(studyID, { showLoader: false })
    })
  }

  const renderQuestionLabel = (answer, index) => {
    const label = answer.question_label || answer.question_id || `Question ${index + 1}`
    return (
      <span
        // Content is sanitized with DOMPurify immediately before rendering.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: formatAndSanitizeHTML(label),
        }}
      />
    )
  }

  return (
    <div className={`dcf-review-drawer${loading ? ' is-loading' : ''}`}>
      {loading && (
        <div className='dcf-drawer-loader-overlay' aria-busy='true' aria-live='polite'>
          <Loader inContainer />
        </div>
      )}
      <section className='review-hero'>
        <div>
          <div className='review-eyebrow'>Data Correction Form</div>
          <h3>{actionLabel}</h3>
          <div className='review-id'>DCF ID: {id ?? '—'}</div>
        </div>
        <span className={`dcf-status ${statusClass}`}>{statusLabel}</span>
      </section>

      <section className='review-section'>
        <div className='section-heading'>
          <div>
            <h4>Request details</h4>
            <p>The target and timing information for this correction.</p>
          </div>
          {canEdit && !isEditing && (
            <Button
              grey
              type='button'
              icon='fas fa-pen'
              content='Edit DCF'
              onClick={() => setIsEditing(true)}
            />
          )}
        </div>

        <div className='review-summary-grid'>
          <div className='summary-card'>
            <div className='summary-label'>Subject ID</div>
            <strong>{displaySubjectId ?? '—'}</strong>
          </div>
          <div className='summary-card'>
            <div className='summary-label'>Instrument Name</div>
            <strong>{displayInstrumentName ?? '—'}</strong>
          </div>
          <div className='summary-card'>
            <div className='summary-label'>Instrument Version</div>
            {isEditing ? (
              <Input
                id='edit-dcf-instrument-version'
                value={draft.instrumentVersion}
                onChange={value => updateDraftField('instrumentVersion', value)}
              />
            ) : (
              <strong>{instrumentVersion ?? '—'}</strong>
            )}
          </div>
          <div className='summary-card'>
            <div className='summary-label'>Start By</div>
            {isEditing ? (
              <Input
                id='edit-dcf-start-by'
                type='datetime-local'
                value={draft.startBy}
                onChange={value => updateDraftField('startBy', value)}
              />
            ) : (
              <strong>{formatDateTime(startBy)}</strong>
            )}
          </div>
          <div className='summary-card'>
            <div className='summary-label'>Completed By</div>
            {isEditing ? (
              <Input
                id='edit-dcf-completed-by'
                type='datetime-local'
                value={draft.completedBy}
                onChange={value => updateDraftField('completedBy', value)}
              />
            ) : (
              <strong>{formatDateTime(completedBy)}</strong>
            )}
          </div>
          <div className='summary-card'>
            <div className='summary-label'>Associated instance</div>
            <strong>
              {instanceId ||
                (action === 'create_new_record' ? 'Created on Apply' : 'Not linked')}
            </strong>
            {!instanceId && action !== 'create_new_record' && (
              <p className='summary-hint'>
                Use Associate instance on the DCF list before Apply.
              </p>
            )}
            {!instanceId && action === 'create_new_record' && (
              <p className='summary-hint'>
                A new database instance will be created when this DCF is applied.
              </p>
            )}
          </div>
          {action === 'flag_questions_only' && (
            <>
              <div className='summary-card'>
                <div className='summary-label'>Transfer ignore</div>
                <strong>
                  {transferIgnore == null ? 'Set both flags to Yes on Apply' : transferIgnore ? 'Yes' : 'No'}
                </strong>
              </div>
              <div className='summary-card'>
                <div className='summary-label'>Reporting ignore</div>
                <strong>
                  {reportingIgnore == null ? 'Set both flags to Yes on Apply' : reportingIgnore ? 'Yes' : 'No'}
                </strong>
              </div>
            </>
          )}
        </div>
      </section>

      {displayErrorMessage && (
        <section className='review-error'>
          <i className='fas fa-exclamation-circle' />
          <div>
            <strong>DCF processing error</strong>
            <p>{displayErrorMessage}</p>
          </div>
        </section>
      )}

      {Array.isArray(answers) && answers.length > 0 && (
        <section className='review-section review-answers'>
          <div className='section-heading'>
            <div>
              <h4>Answers</h4>
              <p>{answers.length} answer{answers.length === 1 ? '' : 's'} included in this DCF.</p>
            </div>
          </div>
          <div className='answer-card-list'>
            {answers.map((answer, idx) => {
              const draftAnswer = draft.answers[idx] || answer
              const hasCorrection =
                answer.original_answer != null &&
                answer.corrected_answer != null &&
                String(answer.original_answer) !== String(answer.corrected_answer)
              return (
                <article className='answer-card' key={answer.question_id || idx}>
                  <div className='answer-number'>
                    {answer.question_number != null ? answer.question_number : idx + 1}
                  </div>
                  <div className='answer-content'>
                    <h5>{renderQuestionLabel(answer, idx)}</h5>
                    <div className='question-code'>{answer.question_id}</div>
                    {isEditing ? (
                      <div className='edit-answer-grid'>
                        <div>
                          <div className='answer-label'>Original answer</div>
                          <div className='readonly-answer'>{answer.original_answer ?? '—'}</div>
                        </div>
                        <div>
                          <div className='answer-label'>Corrected answer</div>
                          {renderCorrectedAnswerEditor(answer, draftAnswer, idx)}
                        </div>
                      </div>
                    ) : (
                      <div className='answer-values'>
                        {hasCorrection && (
                          <div className='answer-value original'>
                            <span>Original</span>
                            <strong>{answer.original_answer}</strong>
                          </div>
                        )}
                        <div className={`answer-value${hasCorrection ? ' corrected' : ''}`}>
                          <span>{hasCorrection ? 'Corrected' : 'Answer'}</span>
                          <strong>{answer.corrected_answer ?? answer.original_answer ?? '—'}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                </article>
              )
            })}
          </div>
        </section>
      )}

      <section className='review-section review-signatures'>
        <div className='section-heading'>
          <div>
            <h4>Stakeholder e-Sign</h4>
            <p>
              Preview the DCF PDF, then click the signature line to place your stamp.
              {canCycleAllRoles
                ? ' Root and DCH Admin cycle through each unsigned block with one click (no role picker).'
                : ' Your signature role comes from your account.'}{' '}
              A drawn mark is optional — name and password re-authentication are required.
            </p>
            {!canSign && (
              <p className='signature-locked-note'>
                Signatures can only be captured while the DCF is in review.
                {status === DCF_STATUS.SUBMITTED && ' Use "Mark In Review" to start signing.'}
              </p>
            )}
          </div>
        </div>
        <div className='signature-card-list saved-summary'>
          {SIGNATURE_ROLES.map(roleConfig => {
            const savedSig = savedSignaturesByRole[roleConfig.role]
            if (!savedSig) return null
            return (
              <article className='signature-card signed' key={roleConfig.role}>
                <div className='signature-card-header'>
                  <h5>{roleConfig.label}</h5>
                  <p className='signature-signed-meta'>
                    <i className='fas fa-check-circle' /> Signed by {savedSig.signer_name}
                    {savedSig.signed_at
                      ? ` on ${moment(savedSig.signed_at).format('MMM D, YYYY h:mm A')}`
                      : ''}
                  </p>
                </div>
                {canSign && (
                  <div className='signature-card-actions'>
                    <Button
                      grey
                      type='button'
                      content='Remove signature'
                      onClick={() => onRemoveSignature(roleConfig.role)}
                    />
                  </div>
                )}
              </article>
            )
          })}
        </div>
        {canSign && (
          <PdfViewerWithSigning
            pdfBytes={esignPdfBytes}
            canSign={canSign}
            savedRoles={Object.keys(savedSignaturesByRole)}
            signingRole={signingRoleConfig?.role || null}
            signingRoleLabel={signingRoleConfig?.label || ''}
            requiresDecision={Boolean(signingRoleConfig?.requiresDecision)}
            roleSourceNote={
              canCycleAllRoles
                ? '(next unsigned — cycle)'
                : '(from your account)'
            }
            prefill={signerPrefill}
            verifyPassword={verifyEsignPassword}
            onSigned={onEsignComplete}
          />
        )}
        {signatureMessage && (
          <p className={`signature-message${signatureMessage.isError ? ' error' : ' success'}`}>
            {signatureMessage.text}
          </p>
        )}
      </section>

      <div className='button-list review-actions flexed end-justified'>
        {isEditing ? (
          <>
            <Button
              grey
              type='button'
              content='Cancel'
              onClick={() => {
                setDraft(createDraft(dcfDetail))
                setIsEditing(false)
              }}
            />
            <Button type='button' content='Save Changes' onClick={onSave} />
          </>
        ) : (
          <>
            <Button grey type='button' content='Close' onClick={closeDrawer} />
            {status !== DCF_STATUS.IN_DRAFT && (
              <>
                <Button grey type='button' content='Download PDF' onClick={onDownloadPdf} />
                <Button grey type='button' content='Download CSV' onClick={onDownloadCsv} />
              </>
            )}
            {canSubmitDraft && (
              <Button type='button' content='Submit DCF' onClick={onSubmitDraft} />
            )}
            {canReject && (
              <Button grey type='button' content='Reject' onClick={onReject} />
            )}
            {canMarkInReview && (
              <Button type='button' content='Mark In Review' onClick={onMarkInReview} />
            )}
            {canCompleteReview && (
              <Button type='button' content='Complete Review' onClick={onCompleteReview} />
            )}
            {isApplyableStatus && (
              <>
                {!canApply && (
                  <span className='apply-blocked-note'>
                    Associate an instrument instance before applying.
                  </span>
                )}
                <Button
                  type='button'
                  content='Apply DCF'
                  disabled={!canApply}
                  onClick={onApply}
                />
              </>
            )}
            {isTerminal && status === DCF_STATUS.COMPLETED && (
              <span className='terminal-note'>
                <i className='fas fa-check-circle' /> Applied
              </span>
            )}
          </>
        )}
      </div>
    </div>
  )
}

DcfReviewDrawer.propTypes = {
  dcfDetail: PropTypes.shape({
    id: PropTypes.string,
    participant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    subject_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    participant_subject_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    instrument_id: PropTypes.string,
    instrument_name: PropTypes.string,
    instrument_title: PropTypes.string,
    instrument_version: PropTypes.string,
    action: PropTypes.string,
    start_by: PropTypes.string,
    started_by: PropTypes.string,
    completed_by: PropTypes.string,
    error_message: PropTypes.string,
    status: PropTypes.string,
    instance_id: PropTypes.string,
    transfer_ignore: PropTypes.bool,
    reporting_ignore: PropTypes.bool,
    answers: PropTypes.arrayOf(
      PropTypes.shape({
        question_id: PropTypes.string,
      }),
    ),
    signatures: PropTypes.arrayOf(
      PropTypes.shape({
        role: PropTypes.string,
        signer_name: PropTypes.string,
        signer_title: PropTypes.string,
        signature_data: PropTypes.string,
        signed_at: PropTypes.string,
        decision: PropTypes.string,
      }),
    ),
  }),
  studyID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  currentUser: PropTypes.shape({
    nickname: PropTypes.string,
    name: PropTypes.string,
    username: PropTypes.string,
    email: PropTypes.string,
    title: PropTypes.string,
    job_title: PropTypes.string,
    jobTitle: PropTypes.string,
    permissions: PropTypes.objectOf(
      PropTypes.shape({
        role_id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
  }),
  loading: PropTypes.bool,
  dcfLookups: PropTypes.shape({
    participants: PropTypes.objectOf(PropTypes.string),
    instruments: PropTypes.objectOf(PropTypes.string),
  }),
  closeDrawer: PropTypes.func,
  updateDcfStatus: PropTypes.func,
  updateDcfContent: PropTypes.func,
  upsertDcfSignatures: PropTypes.func,
  deleteDcfSignature: PropTypes.func,
  applyDcf: PropTypes.func,
  downloadDcf: PropTypes.func,
  downloadDcfCsv: PropTypes.func,
  fetchDcfPdfBytes: PropTypes.func,
  fetchReauthPubkey: PropTypes.func,
  postSigningReauth: PropTypes.func,
  uploadSignedDcfPdf: PropTypes.func,
  fetchDcfList: PropTypes.func,
  fetchDcfDetail: PropTypes.func,
  fetchInstrumentSurvey: PropTypes.func,
}

export default DcfReviewDrawer
